<template>
  <div class="br-releases">
    <v-alert v-if="loadError" type="error">{{ loadError }}</v-alert>
    <v-alert v-if="message" type="success" dismissible @input="message = ''">
      {{ message }}
    </v-alert>

    <v-card class="mb-4">
      <v-card-title class="br">Search BR releases</v-card-title>
      <v-card-text>
        <v-form ref="searchForm" @submit.prevent="search">
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="filters.snumber"
                label="Release s/n"
                inputmode="numeric"
                maxlength="19"
                :rules="serialRules"
                :disabled="saving || deleting"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <date-input
                v-model="filters.release_date"
                label="Released on or after (UTC)"
                :disabled="saving || deleting"
                clearable
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-btn
                color="primary"
                type="submit"
                :loading="loading"
                :disabled="saving || deleting"
                class="mr-2"
              >
                Search
              </v-btn>
              <v-btn text :disabled="saving || deleting" @click="resetSearch">
                Reset
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="releases"
      item-key="snumber"
      :loading="loading"
      :options.sync="options"
      :server-items-length="total"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      class="elevation-1"
      no-data-text="No releases found."
      @dblclick:row="onDoubleClick"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title><h2 class="br">BR Releases</h2></v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            :disabled="loading || saving || deleting"
            aria-label="Refresh BR releases"
            @click="initialize"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:item.release_date="{ item }">{{
        formatDate(item.release_date)
      }}</template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          :disabled="loading || saving || deleting"
          :aria-label="'Edit BR release ' + item.snumber"
          @click.stop="editItem(item)"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :disabled="loading || saving || deleting"
          :aria-label="'Delete BR release ' + item.snumber"
          @click.stop="deleteItem(item)"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" :persistent="saving" max-width="500px">
      <v-card>
        <v-form ref="editForm" @submit.prevent="save">
          <v-card-title>BR Release</v-card-title>
          <v-card-text>
            <v-alert v-if="saveError" type="error">{{ saveError }}</v-alert>
            <v-text-field
              :value="editedItem.snumber"
              label="Release s/n"
              readonly
            />
            <date-input
              v-model="editedItem.release_date"
              label="Release date (UTC)"
              required
              :disabled="saving"
            />
            <p class="mb-0">
              Changing this date affects which subscriptions cover this release.
              Dates must stay in release serial number order.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="saving" @click="dialog = false">
              Cancel
            </v-btn>
            <v-btn color="primary" text type="submit" :loading="saving">
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" :persistent="deleting" max-width="500px">
      <v-card>
        <v-card-title>Delete BR release</v-card-title>
        <v-card-text>
          <v-alert v-if="deleteError" type="error">{{ deleteError }}</v-alert>
          <p>
            Delete release <strong>{{ deletedItem.snumber }}</strong>
            dated {{ formatDate(deletedItem.release_date) }}?
          </p>
          <p class="mb-0">
            License checks for this release will use the next registered
            release's date. If there is no later release, this version will no
            longer be authorized.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deleting" @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" text :loading="deleting" @click="confirmDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DateInput from "../components/DateInput.vue";
import { admin_http_client } from "../services/api.js";
import { formatDate, parseDate } from "../utils/dates.js";

const emptyFilters = () => ({ snumber: "", release_date: "" });
// Opening the page or resetting a search shows the latest 100 releases.
const defaultOptions = () => ({
  page: 1,
  itemsPerPage: 100,
  sortBy: ["snumber"],
  sortDesc: [true]
});
// PostgreSQL bigint upper bound. Compare decimal text without rounding in JS.
const MAX_RELEASE_SNUMBER = "9223372036854775807";
const validSerial = value => {
  const serial = String(value || "").trim();
  return (
    !serial ||
    (/^[1-9][0-9]*$/.test(serial) &&
      (serial.length < MAX_RELEASE_SNUMBER.length ||
        (serial.length === MAX_RELEASE_SNUMBER.length &&
          serial <= MAX_RELEASE_SNUMBER)))
  );
};

export default {
  name: "br_releases",
  components: { DateInput },
  data: () => ({
    filters: emptyFilters(),
    appliedFilters: {},
    releases: [],
    total: 0,
    options: defaultOptions(),
    headers: [
      { text: "Release s/n", value: "snumber" },
      { text: "Release date (UTC)", value: "release_date" },
      { text: "Actions", value: "actions", sortable: false }
    ],
    serialRules: [
      value => validSerial(value) || "Enter a positive release serial number."
    ],
    loading: false,
    saving: false,
    deleting: false,
    dialog: false,
    deleteDialog: false,
    loadError: "",
    saveError: "",
    deleteError: "",
    message: "",
    requestNumber: 0,
    editedItem: { snumber: null, release_date: "" },
    deletedItem: { snumber: null, release_date: "" }
  }),
  watch: {
    options: {
      deep: true,
      immediate: true,
      handler() {
        this.initialize();
      }
    }
  },
  beforeDestroy() {
    this.requestNumber++;
  },
  methods: {
    formatDate,
    async initialize() {
      const request = ++this.requestNumber;
      this.loading = true;
      this.loadError = "";
      try {
        const response = await admin_http_client.get("/br_releases", {
          params: {
            ...this.appliedFilters,
            page: this.options.page,
            per_page: this.options.itemsPerPage,
            sort_by: this.options.sortBy[0] || "snumber",
            descending: this.options.sortBy.length
              ? !!this.options.sortDesc[0]
              : true
          }
        });
        if (request !== this.requestNumber) return;
        // The API returns numeric serials. Never edit/delete a bigint that JSON
        // parsing may have rounded beyond JavaScript's exact integer range.
        if (
          response.data.items.some(item => !Number.isSafeInteger(item.snumber))
        ) {
          this.releases = [];
          this.total = 0;
          this.loadError =
            "A release serial exceeds the browser's exact integer range. These records cannot be managed here.";
          return;
        }
        this.releases = response.data.items;
        this.total = response.data.total;
        // Editing a filtered date or deleting the last row can remove a page.
        const lastPage = Math.max(
          1,
          Math.ceil(this.total / this.options.itemsPerPage)
        );
        if (this.options.page > lastPage) this.options.page = lastPage;
      } catch (error) {
        if (request !== this.requestNumber) return;
        this.releases = [];
        this.total = 0;
        this.loadError = this.errorMessage(
          error,
          "Unable to load releases. Please try again."
        );
      } finally {
        if (request === this.requestNumber) this.loading = false;
      }
    },
    search() {
      if (this.saving || this.deleting || !this.$refs.searchForm.validate())
        return;
      const params = {};
      const serial = (this.filters.snumber || "").trim();
      if (serial) params.snumber = serial;
      const date = parseDate(this.filters.release_date);
      if (date) params.release_date = date;
      this.appliedFilters = params;
      this.message = "";
      if (this.options.page !== 1) this.options.page = 1;
      else this.initialize();
    },
    resetSearch() {
      if (this.saving || this.deleting) return;
      // Ignore responses still arriving for the previous search.
      this.requestNumber++;
      this.filters = emptyFilters();
      this.appliedFilters = {};
      this.releases = [];
      this.total = 0;
      this.loading = false;
      this.loadError = "";
      this.message = "";
      // Replacing the options triggers the watcher to reload the default page.
      this.options = defaultOptions();
      this.$nextTick(() => this.$refs.searchForm.resetValidation());
    },
    editItem(item) {
      if (this.loading || this.saving || this.deleting) return;
      this.editedItem = {
        snumber: item.snumber,
        release_date: formatDate(item.release_date)
      };
      this.saveError = "";
      this.message = "";
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.editForm) this.$refs.editForm.resetValidation();
      });
    },
    onDoubleClick(event, row) {
      this.editItem(row.item);
    },
    deleteItem(item) {
      if (this.loading || this.saving || this.deleting) return;
      this.deletedItem = { ...item };
      this.deleteError = "";
      this.message = "";
      this.deleteDialog = true;
    },
    errorMessage(error, fallback) {
      const detail =
        error.response && error.response.data && error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) return detail.map(item => item.msg).join("; ");
      if (error.response && error.response.status === 403)
        return "Access denied. Check your administrator account and the nginx IP allowlist.";
      return fallback;
    },
    async save() {
      if (this.saving || !this.$refs.editForm.validate()) return;
      this.saving = true;
      this.saveError = "";
      const serial = this.editedItem.snumber;
      try {
        await admin_http_client.put(`/br_releases/${serial}`, {
          release_date: parseDate(this.editedItem.release_date)
        });
        this.dialog = false;
        this.message = `BR release ${serial} has been updated.`;
        await this.initialize();
      } catch (error) {
        this.saveError = this.errorMessage(
          error,
          "Unable to save the release. Please try again."
        );
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete() {
      if (this.deleting || !this.deleteDialog) return;
      this.deleting = true;
      this.deleteError = "";
      const serial = this.deletedItem.snumber;
      try {
        await admin_http_client.delete(`/br_releases/${serial}`);
        this.deleteDialog = false;
        this.message = `BR release ${serial} has been deleted.`;
        await this.initialize();
      } catch (error) {
        this.deleteError = this.errorMessage(
          error,
          "Unable to delete the release. Please try again."
        );
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>
